import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { getSiteUrl } from '@/utils/seo';

export default function GuideArticlePage() {
  const { t } = useTranslation();
  const siteUrl = getSiteUrl();

  const guideSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t('guide.title'),
    description: t('guide.meta_desc'),
    image: 'https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20a%20hands%20holding%20a%20smartphone%20displaying%20colorful%20product%20grid%20against%20a%20cozy%20cafe%20background%20with%20warm%20ambient%20lighting%2C%20modern%20digital%20shopping%20lifestyle%20aesthetic%2C%20soft%20bokeh%20effect%20on%20background%20elements%2C%20contemporary%20tech%20meets%20commerce%20visual%20with%20inviting%20warm%20atmosphere&width=600&height=450&seq=kg-art-card2-v2&orientation=landscape',
    datePublished: '2026-07-07',
    dateModified: '2026-07-07',
    author: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/poradnik-zakupowy-kakobuy',
    },
    inLanguage: 'pl',
  };

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={guideSchema} />
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-4xl mx-auto px-4 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 tracking-tight whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link
              to="/artykul/kakobuy-sheet-poradnik"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-text-line"></i>
              {t('nav.article')}
            </Link>
            <Link
              to="/bezpieczne-zakupy-kakobuy"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-shield-check-line"></i>
              {t('nav.safety')}
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.back_home')}
            </Link>
          </nav>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 md:px-10 py-12 md:py-16">
        <header className="mb-10">
          <div className="flex items-center gap-4 text-sm text-foreground-500 mb-4">
            <time dateTime="2026-07-07">{t('guide.date')}</time>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{t('guide.author')}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
            {t('guide.title')}
          </h1>
        </header>

        <div className="space-y-8">
          <section>
            <p className="text-lg text-foreground-700 leading-relaxed">
              {t('guide.intro')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('guide.section1_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section1_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('guide.section1_p2')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('guide.section2_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section2_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section2_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('guide.section2_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('guide.section3_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section3_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section3_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('guide.section3_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('guide.section4_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section4_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.section4_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('guide.section4_p3')}
            </p>
          </section>

          <section className="bg-background-100 rounded-2xl p-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('guide.conclusion_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('guide.conclusion_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('guide.conclusion_p2')}{' '}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline">
                {t('guide.conclusion_link')}
              </Link>
              {t('guide.conclusion_suffix')}
            </p>
          </section>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              {t('article.back_home')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>

      <footer className="border-t border-background-200 py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-10 text-center">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <p className="text-xs text-foreground-500 mt-2">© 2026 kakobuyfinds.website. Wszelkie prawa zastrzeżone.</p>
        </div>
      </footer>
    </main>
  );
}